import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import styles from './NewEventPage.module.css';
import { useUser } from '../context/UserContext';
import { canManageProgram } from '../hooks/useProgramsApi';

const NewEventPage = () => {
  const { programId } = useParams();
  const { userClubs } = useUser();
  const navigate = useNavigate();
  
  // State for form fields
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState('');
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [locationName, setLocationName] = useState('');
  const [street, setStreet] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [coordinates, setCoordinates] = useState([0, 0]); // Coordinates [longitude, latitude]
  const [equipment, setEquipment] = useState([]);
  const [taskList, setTaskList] = useState([{ title: '', description: '', dueDate: '', status: 'pending' }]);
  const [attendees, setAttendees] = useState([]);

  // Permission state
  const [programPermissions, setProgramPermissions] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(true);
  const [resolvedClubId, setResolvedClubId] = useState(null);

  // Authorization logic - more restrictive to prevent backend permission issues
  const canManage = useMemo(() => {
    // Check if user has club management role
    const hasClubRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const membershipClubId = m.club?._id || m.club;
      const role = (m.role || '').toLowerCase();
      const isTargetClub = String(membershipClubId) === String(resolvedClubId);
      const isOwnerOrManager = ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
      
      return isTargetClub && isOwnerOrManager;
    });

    // Additional check: Look for program-specific management roles in userClubs
    const hasProgramRole = Array.isArray(userClubs) && userClubs.some((m) => {
      const role = (m.role || '').toLowerCase();
      // Only allow specific high-level program roles, not regular members
      const isProgramManager = ['programdirector', 'programadmin', 'headcoach'].includes(role);
      return isProgramManager;
    });

    // Check if user has program management permissions (but be more restrictive)
    const hasProgramPermissions = canManageProgram(programPermissions);
    
    // Additional validation: Even if backend says they have permissions,
    // double-check they're not just a regular member
    const isOnlyMember = Array.isArray(userClubs) && userClubs.every((m) => {
      const role = (m.role || '').toLowerCase();
      return role === 'member' || role === 'player' || role === 'observer';
    });
    
    // If user is only a member across all clubs, deny access regardless of backend permissions
    if (isOnlyMember) {
      return false;
    }

    return hasClubRole || hasProgramRole || hasProgramPermissions;
  }, [userClubs, resolvedClubId, programPermissions]);

  // Fetch program details to get clubId
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramDetails = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}`);
        
        if (response.ok) {
          const program = await response.json();
          
          // Extract clubId correctly (handle both string and object cases)
          const extractedClubId = program.clubId && typeof program.clubId === 'object' 
            ? program.clubId._id 
            : program.clubId;
          setResolvedClubId(extractedClubId);
        } else {
          console.error('Failed to fetch program details, status:', response.status);
          
          // Fallback: Try to find matching club from user's memberships
          if (Array.isArray(userClubs) && userClubs.length > 0) {
            const managerClub = userClubs.find(m => {
              const role = (m.role || '').toLowerCase();
              return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
            });
            
            if (managerClub) {
              const fallbackClubId = managerClub.club?._id || managerClub.club;
              setResolvedClubId(fallbackClubId);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching program details:', error);
        
        // Fallback: Try to find matching club from user's memberships
        if (Array.isArray(userClubs) && userClubs.length > 0) {
          const managerClub = userClubs.find(m => {
            const role = (m.role || '').toLowerCase();
            return ['owner', 'creator', 'clubowner', 'clubmanager', 'manager', 'admin'].includes(role);
          });
          
          if (managerClub) {
            const fallbackClubId = managerClub.club?._id || managerClub.club;
            setResolvedClubId(fallbackClubId);
          }
        }
      }
    };
    
    fetchProgramDetails();
  }, [programId, userClubs]);

  // Fetch program permissions for current user
  useEffect(() => {
    if (!programId) return;
    
    const fetchProgramPermissions = async () => {
      try {
        setPermissionsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/programs/${programId}/permissions`);
        if (response.ok) {
          const data = await response.json();
          setProgramPermissions(data?.permissions || []);
        } else {
          console.log('Permissions endpoint not available, using club role only');
          setProgramPermissions([]);
        }
      } catch (error) {
        console.error('Error fetching program permissions:', error);
        setProgramPermissions([]);
      } finally {
        setPermissionsLoading(false);
      }
    };

    fetchProgramPermissions();
  }, [programId]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if user has management permissions
    if (!canManage) {
      alert('You need management permissions to create events. Only club managers and program staff can create events.');
      return;
    }

    const newEvent = {
        title,
        description,
        type,
        dateTime: { // This is the key structure
          start: startDateTime,
          end: endDateTime,
        },
        location: {
          name: locationName,
          address: { street, city, state, zipCode, coordinates },
        },
        equipment,
        tasks: taskList,
        attendees,
      };
      

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/events/${programId}`, newEvent);
      alert('Event created successfully!');
      navigate(`/programs/${programId}/events`); // Redirect to events page
    } catch (error) {
      console.error('Error creating event:', error);
      if (error.response?.status === 403) {
        alert('You don\'t have permission to create events.');
      } else {
        alert('Error creating event');
      }
    }
  };

  // Add new task
  const addTask = () => {
    setTaskList([...taskList, { title: '', description: '', dueDate: '', status: 'pending' }]);
  };

  // Update a specific task
  const handleTaskChange = (index, field, value) => {
    const updatedTasks = taskList.map((task, i) => (i === index ? { ...task, [field]: value } : task));
    setTaskList(updatedTasks);
  };

  // Add equipment item
  const handleEquipmentChange = (e) => {
    setEquipment(e.target.value.split(',').map(item => item.trim()));
  };

  // Show loading state while permissions are being fetched
  if (permissionsLoading) {
    return (
      <div className={styles.newEventPage}>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <p>Loading permissions...</p>
        </div>
      </div>
    );
  }

  // Show access denied message for unauthorized users
  if (!canManage) {
    return (
      <div className={styles.newEventPage}>
        <div style={{ textAlign: 'center', marginTop: '50px' }}>
          <h2 style={{ color: '#dc3545' }}>Access Denied</h2>
          <p>You need management permissions to create events.</p>
          <p>Only club managers and program staff can create events.</p>
          <button 
            onClick={() => navigate(`/program/${programId}/events`)}
            style={{ 
              padding: '10px 20px', 
              backgroundColor: '#007bff', 
              color: 'white', 
              border: 'none', 
              borderRadius: '4px',
              cursor: 'pointer',
              marginTop: '20px'
            }}
          >
            Go Back to Events
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.newEventPage}>
      <h1>Create a New Event</h1>
      <form onSubmit={handleSubmit} className={styles.eventForm}>
        <div className={styles.formGroup}>
          <label htmlFor="title">Title</label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="description">Description</label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="type">Event Type</label>
          <select id="type" value={type} onChange={(e) => setType(e.target.value)} required>
            <option value="">Select type</option>
            <option value="practice">Practice</option>
            <option value="game">Game</option>
            <option value="tournament">Tournament</option>
          </select>
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="startDateTime">Start Time</label>
          <input
            type="datetime-local"
            id="startDateTime"
            value={startDateTime}
            onChange={(e) => setStartDateTime(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="endDateTime">End Time</label>
          <input
            type="datetime-local"
            id="endDateTime"
            value={endDateTime}
            onChange={(e) => setEndDateTime(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="locationName">Location Name</label>
          <input
            type="text"
            id="locationName"
            value={locationName}
            onChange={(e) => setLocationName(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="street">Street</label>
          <input
            type="text"
            id="street"
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="city">City</label>
          <input
            type="text"
            id="city"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="state">State</label>
          <input
            type="text"
            id="state"
            value={state}
            onChange={(e) => setState(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="zipCode">Zip Code</label>
          <input
            type="text"
            id="zipCode"
            value={zipCode}
            onChange={(e) => setZipCode(e.target.value)}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="coordinates">Coordinates</label>
          <input
            type="text"
            id="coordinates"
            placeholder="Longitude, Latitude"
            value={coordinates.join(', ')}
            onChange={(e) => setCoordinates(e.target.value.split(',').map(Number))}
            required
          />
        </div>

        <div className={styles.formGroup}>
          <label htmlFor="equipment">Equipment (comma-separated)</label>
          <input
            type="text"
            id="equipment"
            value={equipment.join(', ')}
            onChange={handleEquipmentChange}
            required
          />
        </div>

        {/* Task Section */}
        <div className={styles.formGroup}>
          <h3>Tasks</h3>
          {taskList.map((task, index) => (
            <div key={index} className={styles.taskItem}>
              <input
                type="text"
                placeholder="Task Title"
                value={task.title}
                onChange={(e) => handleTaskChange(index, 'title', e.target.value)}
                required
              />
              <textarea
                placeholder="Task Description"
                value={task.description}
                onChange={(e) => handleTaskChange(index, 'description', e.target.value)}
                required
              />
              <input
                type="datetime-local"
                value={task.dueDate}
                onChange={(e) => handleTaskChange(index, 'dueDate', e.target.value)}
                required
              />
            </div>
          ))}
          <button type="button" onClick={addTask} className={styles.addButton}>Add Task</button>
        </div>

        <button type="submit" className={styles.submitButton}>
          Create Event
        </button>
      </form>
    </div>
  );
};

export default NewEventPage;
